// src/pages/KYC.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function KYC() {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ pan: user?.pan || '', gstin: user?.gstin || '', address: user?.address || '', document_type: 'Aadhaar', document_number: '', declaration: false });
  const [documentFile, setDocumentFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/kyc/status').then(r => setStatus(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!form.declaration) return toast.error('Please accept the declaration');
    if (!form.pan || !form.address) return toast.error('PAN and address required');

    const payload = new FormData();
    payload.append('pan', form.pan);
    payload.append('gstin', form.gstin || '');
    payload.append('address', form.address);
    payload.append('document_type', form.document_type);
    payload.append('document_number', form.document_number || '');
    payload.append('declaration', String(form.declaration));
    if (documentFile) payload.append('document', documentFile);

    setLoading(true);
    try {
      await api.post('/kyc/submit', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refreshUser();
      toast.success('KYC submitted! Verification takes 1-2 business days.');
      const r = await api.get('/kyc/status');
      setStatus(r.data);
    } catch (err) { toast.error(err.response?.data?.message || 'Submission failed'); }
    finally { setLoading(false); }
  };

  const statusColor = { verified: 'var(--neon-green)', pending: 'var(--neon-yellow)', under_review: 'var(--neon-cyan)', rejected: 'var(--neon-pink)' };
  const kyc_status = status?.kyc_status || user?.kyc_status || 'pending';

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '3rem 2.5rem' }}>
      <div className="section-tag">KYC Verification</div>
      <h1 className="section-title">KYC <span style={{ color: 'var(--neon-orange)' }}>VERIFICATION</span></h1>

      {/* STATUS */}
      <div className="card" style={{ marginTop: '1.5rem', borderTop: `2px solid ${statusColor[kyc_status] || 'var(--muted)'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '2px', marginBottom: '0.4rem' }}>CURRENT STATUS</div>
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '2rem', color: statusColor[kyc_status], letterSpacing: '2px' }}>{kyc_status.toUpperCase().replace('_', ' ')}</div>
          {status?.verified_at && <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--muted)', marginTop: '0.3rem' }}>Verified: {new Date(status.verified_at).toLocaleDateString('en-IN')}</div>}
          {status?.submitted_at && <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--muted)', marginTop: '0.2rem' }}>Submitted: {new Date(status.submitted_at).toLocaleDateString('en-IN')}</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          {status?.gci_id && <div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '2px' }}>GCI REGISTRY ID</div>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.9rem', color: 'var(--neon-green)', marginTop: '0.2rem' }}>{status.gci_id}</div></div>}
        </div>
      </div>

      {kyc_status === 'verified' ? (
        <div className="card" style={{ marginTop: '1.5rem', textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.5rem', letterSpacing: '2px', color: 'var(--neon-green)', marginBottom: '0.5rem' }}>KYC VERIFIED</div>
          <p style={{ color: 'var(--muted)', fontFamily: 'Space Mono, monospace', fontSize: '0.75rem', lineHeight: '1.8' }}>Your identity is verified. You can trade on CarbonX. GCI Registry ID: <span style={{ color: 'var(--neon-green)' }}>{status?.gci_id}</span></p>
        </div>
      ) : (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.3rem', letterSpacing: '2px', marginBottom: '1.5rem' }}>SUBMIT KYC DOCUMENTS</div>

          {kyc_status === 'under_review' && (
            <div style={{ background: 'rgba(0,229,255,0.07)', border: '1px solid rgba(0,229,255,0.3)', padding: '1rem', marginBottom: '1.5rem', fontFamily: 'Space Mono, monospace', fontSize: '0.72rem', color: 'var(--neon-cyan)' }}>
              ℹ Your KYC is under review. Admin will verify within 1-2 business days. You'll be notified once approved.
            </div>
          )}

          <div className="form-group"><label className="form-label">PAN Number</label>
            <input className="form-input" value={form.pan} onChange={e => setForm({ ...form, pan: e.target.value.toUpperCase() })} maxLength={10} placeholder="ABCDE1234F" /></div>
          <div className="form-group"><label className="form-label">GSTIN (if applicable)</label>
            <input className="form-input" value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value.toUpperCase() })} maxLength={15} placeholder="27XXXXX1234X1ZX" /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group"><label className="form-label">Identity Document Type</label>
              <select className="form-select" value={form.document_type} onChange={e => setForm({ ...form, document_type: e.target.value })}>
                <option>Aadhaar</option><option>Passport</option><option>Driving License</option><option>Voter ID</option>
              </select></div>
            <div className="form-group"><label className="form-label">Document Number</label>
              <input className="form-input" value={form.document_number} onChange={e => setForm({ ...form, document_number: e.target.value })} placeholder="XXXX XXXX XXXX" /></div>
          </div>
          <div className="form-group"><label className="form-label">Upload Document (PDF/JPG/PNG, max 10MB)</label>
            <input className="form-input" type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={e => setDocumentFile(e.target.files?.[0] || null)} /></div>
          <div className="form-group"><label className="form-label">Registered Address</label>
            <textarea className="form-textarea" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} rows={3} /></div>

          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', padding: '1rem', marginBottom: '1.5rem', fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--muted)', lineHeight: '1.8' }}>
            📋 Your documents will be reviewed by a BEE-accredited verification agency. KYC is required under PMLA 2002 and CCTS 2023. Data stored securely per IT Act 2000.
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.8rem', cursor: 'pointer', marginBottom: '1.5rem', fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--muted)', lineHeight: '1.7' }}>
            <input type="checkbox" checked={form.declaration} onChange={e => setForm({ ...form, declaration: e.target.checked })} style={{ marginTop: '2px', accentColor: 'var(--neon-green)', width: '14px', height: '14px' }} />
            I declare that the information provided is accurate and I authorize CarbonX to verify my identity with relevant authorities including UIDAI, MCA, and GCI Registry.
          </label>

          <button className="btn btn-orange" onClick={submit} disabled={loading || kyc_status === 'under_review'} style={{ width: '100%', justifyContent: 'center', padding: '0.9rem' }}>
            {loading ? 'SUBMITTING...' : kyc_status === 'under_review' ? 'UNDER REVIEW' : 'SUBMIT FOR VERIFICATION →'}
          </button>
        </div>
      )}
    </div>
  );
}
